// src/pages/usuarios/UsuariosSubastaPage.tsx
// Panel de Aprobación de Usuarios para Subastas en Vivo

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  UserSubasta,
  PageResponse,
  aprobarUsuario,
  listarUsuarios,
  revocarUsuario,
} from "../../api/userSubastaApi";

const PAGE_SIZE = 30;

function getAdminToken() {
  return (
    localStorage.getItem("fg_admin_token") ||
    localStorage.getItem("fg_token") ||
    ""
  );
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function UsuariosSubastaPage() {
  const [pageData, setPageData] = useState<PageResponse<UserSubasta> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── LOAD ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    async (p = 0, query = q) => {
      setLoading(true);
      try {
        const data = await listarUsuarios(p, PAGE_SIZE, query);
        setPageData(data);
        setPage(p);
      } catch (e: any) {
        showError(e?.response?.data?.message || "Error cargando usuarios");
      } finally {
        setLoading(false);
      }
    },
    [q],
  );

  useEffect(() => {
    load(0, q);
  }, []);

  // ─── BÚSQUEDA CON DEBOUNCE ─────────────────────────────────────────────────
  const handleSearch = (v: string) => {
    setQInput(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setQ(v);
      load(0, v);
    }, 450);
  };

  // ─── APROBAR / REVOCAR ─────────────────────────────────────────────────────
  const handleToggle = async (user: UserSubasta) => {
    setActionLoading(user.id);
    try {
      const updated = user.aprobadoSubastar
        ? await revocarUsuario(user.id)
        : await aprobarUsuario(user.id);

      setPageData((prev) =>
        prev
          ? {
              ...prev,
              content: prev.content.map((u) =>
                u.id === updated.id ? updated : u,
              ),
            }
          : prev,
      );

      showSuccess(
        updated.aprobadoSubastar
          ? `✓ ${updated.firstName} aprobado para subastar`
          : `${updated.firstName} — aprobación revocada`,
      );
    } catch (e: any) {
      showError(e?.response?.data?.message || "Error actualizando usuario");
    } finally {
      setActionLoading(null);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 4000);
  };
  const showError = (msg: string) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 5000);
  };

  const users = pageData?.content ?? [];
  const totalPages = pageData?.totalPages ?? 0;
  const totalElements = pageData?.totalElements ?? 0;
  const aprobados = users.filter((u) => u.aprobadoSubastar).length;

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{ padding: 24, minHeight: "100vh", backgroundColor: "#f4f5f4" }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontWeight: 800,
              color: "#0f3644",
              fontSize: 22,
            }}
          >
            👥 Aprobación Usuarios — Subastas
          </h2>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>
            Activa o desactiva el permiso para pujar en subastas en vivo. Solo
            los usuarios con comprobante de pago verificado deben ser aprobados.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, color: "#666" }}>
            Total: <strong>{totalElements}</strong>
          </div>
          <div style={{ fontSize: 13, color: "#0cbb1d" }}>
            Aprobados (pág): <strong>{aprobados}</strong>
          </div>
        </div>
      </div>

      {/* INFO BOX */}
      <div
        style={{
          padding: "10px 16px",
          borderRadius: 10,
          marginBottom: 16,
          backgroundColor: "#e8f5e9",
          border: "1px solid #0cbb1d",
          fontSize: 13,
          color: "#006b4c",
        }}
      >
        <strong>Flujo de aprobación:</strong> El usuario se registra en la app →
        envía comprobante de pago al WhatsApp <strong>3053841378</strong> → el
        Admin verifica y hace click en <strong>Aprobar</strong>. Solo entonces
        podrá pujar en subastas en vivo.
      </div>

      {/* BUSCADOR */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <input
          value={qInput}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="🔍 Buscar por nombre, email o teléfono..."
          style={{
            flex: 1,
            padding: "9px 14px",
            borderRadius: 10,
            border: "1px solid #dee2e6",
            fontSize: 14,
            maxWidth: 420,
          }}
        />
        {q && (
          <button
            onClick={() => {
              setQInput("");
              setQ("");
              load(0, "");
            }}
            style={{
              padding: "8px 14px",
              borderRadius: 10,
              border: "1px solid #dee2e6",
              background: "#fff",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            ✕ Limpiar
          </button>
        )}
      </div>

      {/* MENSAJES */}
      {successMsg && <div style={alertStyle("success")}>{successMsg}</div>}
      {errorMsg && <div style={alertStyle("error")}>{errorMsg}</div>}

      {/* TABLA */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          Cargando usuarios...
        </div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#aaa" }}>
          {q ? `Sin resultados para "${q}"` : "No hay usuarios registrados."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                {[
                  "Usuario",
                  "Email / Teléfono",
                  "Paleta",
                  "Registro",
                  "Estado",
                  "Acción",
                ].map((h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const name =
                  `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—";
                const isLoading = actionLoading === u.id;
                return (
                  <tr
                    key={u.id}
                    style={{
                      background: u.aprobadoSubastar ? "#f0fff4" : "#fff",
                      borderLeft: `4px solid ${u.aprobadoSubastar ? "#0cbb1d" : "#dee2e6"}`,
                    }}
                  >
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        {/* Avatar */}
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: u.aprobadoSubastar
                              ? "#0f3644"
                              : "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            fontWeight: 800,
                            color: u.aprobadoSubastar ? "#0cbb1d" : "#888",
                            flexShrink: 0,
                            backgroundImage: u.avatarUrl
                              ? `url(${u.avatarUrl})`
                              : undefined,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        >
                          {!u.avatarUrl && (name[0] || "?").toUpperCase()}
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: "#1a3a2a",
                            }}
                          >
                            {name}
                          </div>
                          <div style={{ fontSize: 11, color: "#888" }}>
                            {u.id.substring(0, 12)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: 13 }}>{u.email || "—"}</div>
                      <div style={{ fontSize: 12, color: "#888" }}>
                        {u.phoneNumber || "—"}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      {u.palletNumber ? (
                        <span
                          style={{
                            fontWeight: 800,
                            fontSize: 15,
                            color: "#f98115",
                          }}
                        >
                          #{String(u.palletNumber).padStart(3, "0")}
                        </span>
                      ) : (
                        <span style={{ color: "#bbb", fontSize: 12 }}>
                          Sin paleta
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "#888" }}>
                      {formatDate(u.createdAt)}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 700,
                          backgroundColor: u.aprobadoSubastar
                            ? "#d4edda"
                            : "#fff3cd",
                          color: u.aprobadoSubastar ? "#006b4c" : "#856404",
                        }}
                      >
                        {u.aprobadoSubastar ? "✓ Aprobado" : "Pendiente"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleToggle(u)}
                        disabled={isLoading}
                        style={{
                          padding: "6px 16px",
                          borderRadius: 8,
                          border: "none",
                          backgroundColor: u.aprobadoSubastar
                            ? "#dc354520"
                            : "#0cbb1d",
                          color: u.aprobadoSubastar ? "#D60D4C" : "#fff",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: isLoading ? "not-allowed" : "pointer",
                          opacity: isLoading ? 0.6 : 1,
                          transition: "all 0.15s",
                        }}
                      >
                        {isLoading
                          ? "..."
                          : u.aprobadoSubastar
                            ? "Revocar"
                            : "Aprobar"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINACIÓN */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginTop: 20,
            alignItems: "center",
          }}
        >
          <button
            onClick={() => load(page - 1)}
            disabled={page === 0}
            style={{ ...paginBtn, opacity: page === 0 ? 0.4 : 1 }}
          >
            ‹ Anterior
          </button>
          <span style={{ fontSize: 13, color: "#666" }}>
            Página {page + 1} de {totalPages}
          </span>
          <button
            onClick={() => load(page + 1)}
            disabled={page >= totalPages - 1}
            style={{ ...paginBtn, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
          >
            Siguiente ›
          </button>
        </div>
      )}
    </div>
  );
}

// ─── ESTILOS ──────────────────────────────────────────────────────────────────
const alertStyle = (type: "success" | "error"): React.CSSProperties => ({
  padding: "10px 16px",
  borderRadius: 10,
  marginBottom: 12,
  fontWeight: 700,
  background: type === "success" ? "#d4edda" : "#f8d7da",
  color: type === "success" ? "#006b4c" : "#D60D4C",
  border: `1px solid ${type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
});
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  borderRadius: 10,
  overflow: "hidden",
  backgroundColor: "#fff",
};
const thStyle: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  color: "#495057",
  borderBottom: "2px solid #dee2e6",
};
const tdStyle: React.CSSProperties = {
  padding: "10px 14px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
};
const paginBtn: React.CSSProperties = {
  padding: "7px 16px",
  borderRadius: 8,
  border: "1px solid #dee2e6",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
};
